import { useEffect } from 'react'
import { useMatches } from '@tanstack/react-router'

/**
 * Breadcrumb configuration for routes
 * Maps route paths to their breadcrumb labels and parent routes
 * Shared with use-breadcrumb-from-route.ts for consistency
 */
const breadcrumbConfig: Record<string, { label: string; parent?: string }> = {
  // Home section routes
  '/home': { label: 'Home' },
  '/dashboard': { label: 'Dashboard', parent: 'Home' },
  '/calendar': { label: 'Calendar', parent: 'Home' },
  '/leads': { label: 'Leads', parent: 'Home' },
  '/events': { label: 'Events', parent: 'Home' },
  '/tasks': { label: 'Tasks', parent: 'Home' },
  '/invoices': { label: 'Invoices', parent: 'Home' },

  // CRM section routes
  '/crm': { label: 'CRM' },
  '/contacts': { label: 'Contacts', parent: 'CRM' },
  '/companies': { label: 'Companies', parent: 'CRM' },
  '/marketing': { label: 'Marketing', parent: 'CRM' },

  // Settings section routes
  '/settings': { label: 'Settings' },
  '/organization': { label: 'Organization', parent: 'Settings' },
  '/staff': { label: 'Staff', parent: 'Settings' },
  '/resources': { label: 'Resources', parent: 'Settings' },
  '/forms': { label: 'Forms', parent: 'Settings' },
  '/email': { label: 'Email', parent: 'Settings' },
  '/payments': { label: 'Payments', parent: 'Settings' },
  '/billing': { label: 'Billing', parent: 'Settings' },
  '/settings/workflows': { label: 'Workflows', parent: 'Settings' },
  '/settings/workflows/new': { label: 'Create Workflow', parent: 'Settings' },
}

/**
 * Custom hook to automatically set document title based on current route
 *
 * Sets the browser tab title to "Cortado Pro - {Page Name}" based on the current route.
 * Handles dynamic routes (e.g., /leads/$id becomes "Cortado Pro - Lead").
 *
 * @example
 * // Automatically updates title based on route
 * function RootComponent() {
 *   useDocumentTitle()
 *   return <Outlet />
 * }
 */
export function useDocumentTitle() {
  const matches = useMatches()

  useEffect(() => {
    // Get the last matched route (current page)
    const currentMatch = matches[matches.length - 1]
    if (!currentMatch) {
      document.title = 'Cortado Pro'
      return
    }

    const pathname = currentMatch.pathname
    // Normalize pathname by removing trailing slash for matching
    const normalizedPathname = pathname.replace(/\/$/, '') || '/'
    const config = breadcrumbConfig[normalizedPathname] || breadcrumbConfig[pathname]

    let pageLabel: string

    if (!config) {
      // Fallback: generate label from pathname
      const segments = pathname.split('/').filter(Boolean)

      // Handle empty segments (e.g., root path "/")
      if (segments.length === 0) {
        document.title = 'Cortado Pro'
        return
      }

      const label = segments[segments.length - 1]

      // Defensive check for undefined/empty label
      if (!label) {
        document.title = 'Cortado Pro'
        return
      }

      // Check if the last segment looks like a UUID, if so use parent
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(label)

      if (isUUID && segments.length > 1) {
        // For dynamic routes like /leads/$id, use singular form of parent
        const parentLabel = segments[segments.length - 2]
        const singularLabel = parentLabel.endsWith('s')
          ? parentLabel.slice(0, -1)
          : parentLabel
        pageLabel = singularLabel.charAt(0).toUpperCase() + singularLabel.slice(1)
      } else {
        // Format the label from the path segment
        pageLabel = label
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ')
      }
    } else {
      pageLabel = config.label
    }

    // Set document title
    document.title = `Cortado Pro - ${pageLabel}`
  }, [matches])
}
